import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import { fetchCops } from "../../api/cop";
import {
  faShieldAlt,
  faChild,
  faLaptop,
  faLock,
  faHeadset,
  faBook,
  faExclamationTriangle,
  faInfoCircle,
  faGlobe,
  faDownload,
  faArrowRight,
  faClockRotateLeft,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";

// ProtectionCard component (unchanged)
const ProtectionCard = ({ icon, title, description, bulletPoints, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{
      y: -8,
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }}
    className="bg-white p-8 rounded-xl shadow-lg transition-all duration-300 border-b-4 border-primary overflow-hidden group relative"
  >
    <div className="absolute -right-10 -top-10 w-20 h-20 bg-gradient-to-br from-primary/30 to-primary/5 rounded-full"></div>
    <div className="flex items-start relative z-10">
      <div className="bg-primary p-4 rounded-lg text-white transform transition-transform group-hover:rotate-6 group-hover:scale-110 flex-shrink-0">
        <FontAwesomeIcon icon={icon} className="text-2xl" />
      </div>
      <div className="ml-5">
        <h3 className="text-h-6 font-bold text-heading mb-3 group-hover:text-primary transition-colors font-nunito-sans">
          {title}
        </h3>
        <p className="text-body-big text-paragraph leading-relaxed font-catamaran">
          {description}
        </p>
        <ul className="mt-4 space-y-2">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex items-center text-accent">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2"></span>
              <span className="text-body font-nunito-sans text-paragraph">
                {point}
              </span>
            </li>
          ))}
        </ul>
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          whileHover={{ opacity: 1, width: "100%" }}
          className="h-0.5 bg-primary/20 mt-4"
        ></motion.div>
      </div>
    </div>
  </motion.div>
);

const ChildOnlineProtection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [activeVideo, setActiveVideo] = useState(null);

  // Fetch COP data using useQuery
  const {
    data: officialGuidelines,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["childOnlineProtection", { lang }],
    queryFn: () => fetchCops({ lang }),
  });

  const protectionMeasures = [
    {
      icon: faShieldAlt,
      title: t("cop.parental_controls.title", "Parental Controls"),
      description: t(
        "cop.parental_controls.description",
        "Set up robust parental controls on devices and applications to monitor and restrict access to inappropriate content."
      ),
      bulletPoints: [
        t(
          "cop.parental_controls.bulletPoints.content_filtering",
          "Content filtering and safe search"
        ),
        t(
          "cop.parental_controls.bulletPoints.time_limits",
          "App & screen time limits"
        ),
        t(
          "cop.parental_controls.bulletPoints.location_tracking",
          "Location tracking for safety"
        ),
      ],
    },
    {
      icon: faHeadset,
      title: t("cop.open_communication.title", "Open Communication"),
      description: t(
        "cop.open_communication.description",
        "Maintain open dialogue with children about their online activities and potential risks."
      ),
      bulletPoints: [
        t(
          "cop.open_communication.bulletPoints.non_judgmental",
          "Non-judgmental conversations"
        ),
        t(
          "cop.open_communication.bulletPoints.check_ins",
          "Regular check-ins about online experiences"
        ),
        t(
          "cop.open_communication.bulletPoints.trust",
          "Building trust through understanding"
        ),
      ],
    },
    {
      icon: faClockRotateLeft,
      title: t("cop.screen_time.title", "Screen Time Management"),
      description: t(
        "cop.screen_time.description",
        "Establish healthy screen time limits and encourage balanced digital habits."
      ),
      bulletPoints: [
        t(
          "cop.screen_time.bulletPoints.device_free",
          "Device-free meals and activities"
        ),
        t(
          "cop.screen_time.bulletPoints.digital_breaks",
          "Scheduled digital breaks"
        ),
        t(
          "cop.screen_time.bulletPoints.quality",
          "Quality over quantity of screen time"
        ),
      ],
    },
    {
      icon: faUserShield,
      title: t("cop.digital_literacy.title", "Digital Literacy Education"),
      description: t(
        "cop.digital_literacy.description",
        "Teach children about responsible internet use and digital citizenship."
      ),
      bulletPoints: [
        t(
          "cop.digital_literacy.bulletPoints.critical_thinking",
          "Critical thinking about online information"
        ),
        t(
          "cop.digital_literacy.bulletPoints.digital_footprint",
          "Understanding digital footprints"
        ),
        t(
          "cop.digital_literacy.bulletPoints.social_media",
          "Responsible social media behavior"
        ),
      ],
    },
  ];

  const onlineRisks = [
    {
      icon: faExclamationTriangle,
      title: t("cop.cyberbullying.title", "Cyberbullying"),
      description: t(
        "cop.cyberbullying.description",
        "Online harassment and bullying through digital platforms."
      ),
      tips: [
        t("cop.cyberbullying.tip1", "Save evidence of bullying messages"),
        t("cop.cyberbullying.tip2", "Block and report abusive users"),
        t("cop.cyberbullying.tip3", "Seek help from trusted adults"),
      ],
    },
    {
      icon: faLock,
      title: t("cop.privacy.title", "Privacy Concerns"),
      description: t(
        "cop.privacy.description",
        "Protecting personal information online."
      ),
      tips: [
        t("cop.privacy.tip1", "Never share personal information"),
        t("cop.privacy.tip2", "Use strong privacy settings"),
        t("cop.privacy.tip3", "Be careful with online friends"),
      ],
    },
  ];

  const closeVideoModal = () => {
    setActiveVideo(null);
  };

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t("cop.title", "Child Online Protection")}
        description={t(
          "cop.subtitle",
          "Ensuring a safe digital environment for children"
        )}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.child_online_protection"), link: null },
        ]}
        backgroundIcons={[faChild, faLaptop, faShieldAlt]}
      />

      <div className="bg-gray-50 min-h-screen py-12">
        <div className="px-dynamic">
          {/* Introduction Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-h-3 font-bold text-heading mb-6 text-center font-catamaran"
            >
              {t("cop.intro.title", "Protecting Children in the Digital Age")}
            </motion.h2>
            <p className="text-body-big text-paragraph leading-relaxed font-nunito-sans">
              {t(
                "cop.intro.description",
                "As children spend more time online, it’s crucial to ensure their safety and well-being in the digital world. Learn about important measures and guidelines for protecting children online."
              )}
            </p>
          </motion.div>

          {/* Protection Measures Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {protectionMeasures.map((measure, index) => (
              <ProtectionCard
                key={index}
                icon={measure.icon}
                title={measure.title}
                description={measure.description}
                bulletPoints={measure.bulletPoints}
                delay={index * 0.1}
              />
            ))}
          </div>

          {/* Online Risks Section */}
          <section className="my-20 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-light/30 to-blue-50 opacity-70 rounded-3xl -z-10"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-light/50 rounded-full opacity-30 blur-2xl -z-10"></div>
            <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-blue-100 rounded-full opacity-40 blur-3xl -z-10"></div>

            <div className="relative py-12">
              <div className="text-center mb-12">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-h-3 font-bold text-heading mb-6 text-center font-catamaran"
                >
                  {t("cop.risks.title", "Understanding Online Risks")}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 text-body-big text-paragraph leading-relaxed font-nunito-sans max-w-3xl mx-auto"
                >
                  {t(
                    "cop.risks.description",
                    "Children face various risks online. Understanding these challenges is the first step to effectively protecting them."
                  )}
                </motion.p>
              </div>

              <div className="grid md:grid-cols-2 gap-10 mb-10">
                {onlineRisks.map((risk, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.2 }}
                    className="relative"
                  >
                    <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 group">
                      <div className="bg-primary p-6 flex items-center relative overflow-hidden">
                        <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                        <div className="absolute left-0 bottom-0 w-16 h-16 bg-white/10 rounded-full -ml-5 -mb-5"></div>
                        <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm mr-4">
                          <FontAwesomeIcon
                            icon={risk.icon}
                            className="text-h-6 text-white"
                          />
                        </div>
                        <h3 className="text-h-6 font-bold text-white font-nunito-sans">
                          {risk.title}
                        </h3>
                      </div>
                      <div className="p-6">
                        <p className="text-body-big text-paragraph leading-relaxed font-nunito-sans">
                          {risk.description}
                        </p>
                        <div>
                          <h4 className="font-bold text-gray-800 mb-3 mt-3 flex items-center font-nunito-sans">
                            <FontAwesomeIcon
                              icon={faShieldAlt}
                              className="text-primary mr-2"
                            />
                            {t("cop.risks.safety_measures", "Safety Measures")}
                          </h4>
                          <ul className="space-y-3">
                            {risk.tips.map((tip, idx) => (
                              <motion.li
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                viewport={{ once: true }}
                                className="flex items-start group/tip"
                              >
                                <div className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center mt-0.5 mr-3 group-hover/tip:bg-primary group-hover/tip:text-white transition-colors">
                                  <span className="text-body font-bold">
                                    {idx + 1}
                                  </span>
                                </div>
                                <span className="text-paragraph text-body group-hover/tip:text-gray-900 transition-colors font-nunito-sans">
                                  {tip}
                                </span>
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/*
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    {
                      value: "37%",
                      label: t(
                        "cop.statistics.cyberbullying",
                        "of young people have experienced cyberbullying"
                      ),
                    },
                    {
                      value: "15%",
                      label: t(
                        "cop.statistics.inappropriate_content",
                        "have been exposed to inappropriate content"
                      ),
                    },
                    {
                      value: "25%",
                      label: t(
                        "cop.statistics.personal_info",
                        "have shared personal information with strangers"
                      ),
                    },
                    {
                      value: "42%",
                      label: t(
                        "cop.statistics.parental_controls",
                        "of parents use parental controls"
                      ),
                    },
                  ].map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-h-5 font-bold text-primary font-nunito-sans">
                        {stat.value}
                      </div>
                      <div className="text-body text-paragraph font-nunito-sans mt-1">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
              */}
            </div>
          </section>

          {/* Resources Section */}
          <motion.div
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7 }}
  className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-8 relative overflow-hidden"
>
  <div className="absolute top-0 right-0 opacity-10">
    <svg
      width="300"
      height="300"
      viewBox="0 0 200 200"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fill="#4F46E5" d="M40,0 L60,0 L100,40 L40,100 L0,60 Z" />
      <circle cx="150" cy="50" r="30" fill="#4F46E5" />
      <rect x="120" y="100" width="60" height="60" fill="#4F46E5" />
    </svg>
  </div>
  <div className="flex items-center justify-between mb-10 relative z-10">
    <div className="flex items-center">
      <div className="bg-primary p-4 rounded-xl text-white mr-5">
        <FontAwesomeIcon icon={faBook} className="text-3xl" />
      </div>
      <div>
        <h2 className="text-h-3 font-bold text-heading font-catamaran">
          {t("cop.resources.title", "Official Safety Guidelines")}
        </h2>
        <p className="text-gray-600 mt-1">
          {t("cop.resources.description")}
        </p>
      </div>
    </div>
    <div className="hidden md:block">
      <span className="bg-primary/10 text-primary px-4 py-1 rounded-full text-sm">
        {t("cop.resources.trusted_sources")}
      </span>
    </div>
  </div>

  <div className="space-y-12 relative z-10">
    {isLoading ? (
      <FullscreenSpinner message={t("cop.resources.loading", "Loading resources...")} className="p-10" />
    ) : error ? (
      <div className="text-center p-10 bg-white rounded-xl shadow-md">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-red-400 text-5xl mb-4"
        />
        <h3 className="text-h-5 font-bold text-gray-600 mb-2">
          {t("cop.resources.error", "Error Loading Resources")}
        </h3>
        <p className="text-gray-500">
          {t(
            "cop.resources.errorMessage",
            "Something went wrong while fetching resources. Please try again later."
          )}
        </p>
      </div>
    ) : (!officialGuidelines || officialGuidelines.length === 0) ? (
      <div className="text-center p-10 bg-white rounded-xl shadow-md">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="text-gray-400 text-5xl mb-4"
        />
        <h3 className="text-h-5 font-bold text-gray-600 mb-2">
          {t("cop.resources.noResources", "No Resources Available")}
        </h3>
        <p className="text-gray-500">
          {t(
            "cop.resources.noDataMessage",
            "There are currently no official guidelines available. Please check back later."
          )}
        </p>
      </div>
    ) : (
      officialGuidelines
        .filter((org) => org.resources && org.resources.length > 0)
        .map((org, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.2 }}
            className="group"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 bg-white p-5 rounded-xl shadow-md">
              <div className="flex items-center">
                <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-primary text-xl"
                  />
                </div>
                <div>
                  <h3 className="text-h-6 font-bold text-gray-800">
                    <a
                      href={org.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors flex items-center"
                    >
                      {org.name}
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1"
                      />
                    </a>
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">
                    {t("cop.resources.standards")}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {org.resources.map((resource, idx) => {
                const resourceUrl = resource.external_url || resource.file_url;
                const isVideo = resource.resource_type === "video";

                return (
                  <motion.div
                    key={idx}
                    whileHover={{
                      y: -5,
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                    }}
                    className="block p-6 bg-white rounded-xl shadow hover:shadow-xl transition-all duration-300 border border-gray-100"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-body-big font-bold text-heading font-nunito-sans mb-2">
                        {resource.translations?.title || "Untitled Resource"}
                      </h4>
                      <div className="flex">
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            resource.resource_type === "pdf"
                              ? "bg-blue-100 text-blue-600"
                              : resource.resource_type === "video"
                              ? "bg-red-100 text-red-600"
                              : resource.resource_type === "webpage"
                              ? "bg-green-100 text-green-600"
                              : resource.resource_type === "toolkit"
                              ? "bg-purple-100 text-purple-600"
                              : resource.resource_type === "infographic"
                              ? "bg-amber-100 text-amber-600"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {resource.resource_type.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <p className="text-body text-paragraph font-catamaran mb-6">
                      {resource.translations?.description || "No description available"}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      {isVideo ? (
                        <button
                          onClick={() => setActiveVideo(resourceUrl)}
                          className="flex items-center text-primary font-medium hover:underline"
                        >
                          <FontAwesomeIcon icon={faHeadset} className="mr-2" />
                          <span>
                            {t("cop.resources.watch_video", "Watch Video")}
                          </span>
                        </button>
                      ) : (
                        <a
                          href={resourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center text-primary font-medium hover:underline"
                        >
                          <FontAwesomeIcon
                            icon={
                              resource.resource_type === "pdf"
                                ? faDownload
                                : resource.resource_type === "webpage"
                                ? faGlobe
                                : resource.resource_type === "toolkit"
                                ? faBook
                                : resource.resource_type === "infographic"
                                ? faInfoCircle
                                : faDownload
                            }
                            className="mr-2"
                          />
                          <span>
                            {resource.resource_type === "pdf"
                              ? t("cop.resources.download_document")
                              : resource.resource_type === "webpage"
                              ? t("cop.resources.visit_webpage")
                              : resource.resource_type === "toolkit"
                              ? t("cop.resources.access_toolkit")
                              : resource.resource_type === "infographic"
                              ? t("cop.resources.view_infographic")
                              : t("cop.resources.access_resource")}
                          </span>
                        </a>
                      )}
                      <motion.a
                        href={resourceUrl}
                        className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center"
                        whileHover={{ rotate: 45 }}
                      >
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="text-primary"
                        />
                      </motion.a>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        ))
    )}
  </div>

  <div className="mt-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 relative z-10">
    <div className="flex items-start">
      <div className="bg-primary text-white p-3 rounded-full mr-4 flex-shrink-0">
        <FontAwesomeIcon icon={faInfoCircle} className="text-xl" />
      </div>
      <div>
        <h4 className="font-bold text-gray-800 mb-2">
          {t("cop.resources.info_notice.title")}
        </h4>
        <p className="text-paragraph mb-4">
          {t("cop.resources.info_notice.description")}
        </p>
      </div>
    </div>
  </div>
</motion.div>
        </div>
      </div>
  

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={closeVideoModal}
        >
          <div
            className="relative w-full max-w-4xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute -top-12 right-0">
              <button
                onClick={closeVideoModal}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2 backdrop-blur-sm transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="bg-black rounded-lg overflow-hidden aspect-video">
              {activeVideo.includes("youtube.com") ? (
                <iframe
                  className="w-full h-full"
                  src={activeVideo.replace("watch?v=", "embed/")}
                  title="Video Player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : activeVideo.includes("vimeo.com") ? (
                <iframe
                  className="w-full h-full"
                  src={`https://player.vimeo.com/video/${activeVideo
                    .split("/")
                    .pop()}`}
                  title="Video Player"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video className="w-full h-full" controls autoPlay>
                  <source src={activeVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChildOnlineProtection;
